import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { SearchField } from '@3dspinit/ui';
import { useSession } from '../context/SessionContext';
import { PRIMARY_NAV, SECONDARY_NAV } from '../lib/routes';
import { setPendingAction } from '../lib/pendingActions';
import { Wordmark } from './Wordmark';

export function Header() {
  const { sessionState, profile, loading, startKeyra } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const signedIn = sessionState === 'KEYRA_VERIFIED' || sessionState === 'SIGNED_IN_UNVERIFIED';
  const initializing = sessionState === 'INITIALIZING';

  function submitSearch(value: string) {
    const q = value.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  }

  function navToProtected(path: string, pending?: Parameters<typeof setPendingAction>[0]) {
    if (pending) setPendingAction(pending);
    if (!signedIn) {
      void startKeyra(path);
      return;
    }
    navigate(path);
  }

  return (
    <header className="site-header">
      <Wordmark />

      <div className="site-header__search">
        <SearchField
          placeholder="Search guitars, clothing, museum objects, parts, rooms…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitSearch(query);
          }}
          onClear={() => setQuery('')}
        />
      </div>

      <div className="site-header__account">
        <nav className="nav" aria-label="Primary">
          {signedIn ? (
            <>
              {PRIMARY_NAV.filter((r) => r.id !== 'library' || signedIn).map((route) => (
                <NavLink
                  key={route.id}
                  to={route.path}
                  className={({ isActive }) => {
                    const createAlias =
                      route.id === 'create' &&
                      (location.pathname === '/create' || location.pathname.startsWith('/workspace'));
                    return isActive || createAlias ? 'active' : undefined;
                  }}
                >
                  {route.label}
                </NavLink>
              ))}
              <details style={{ position: 'relative' }}>
                <summary className="linkish" style={{ listStyle: 'none', cursor: 'pointer' }}>
                  More
                </summary>
                <div
                  className="panel"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: '0.5rem',
                    minWidth: 160,
                    zIndex: 30,
                  }}
                >
                  {SECONDARY_NAV.map((route) => (
                    <NavLink
                      key={route.id}
                      to={route.path}
                      style={{ display: 'block', padding: '0.35rem 0' }}
                    >
                      {route.label}
                    </NavLink>
                  ))}
                </div>
              </details>
              <NavLink to="/profile">{profile?.username ? `@${profile.username}` : 'Profile'}</NavLink>
            </>
          ) : (
            <>
              <NavLink to="/">Explore</NavLink>
              <NavLink to="/connect">Connect</NavLink>
              <button
                type="button"
                className="linkish"
                onClick={() => navToProtected('/create', { type: 'create' })}
              >
                Create
              </button>
              <button
                type="button"
                className="btn"
                disabled={initializing || loading}
                onClick={() => navToProtected('/library')}
              >
                My Library
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={initializing || loading}
                onClick={() => void startKeyra()}
              >
                Sign in
              </button>
            </>
          )}
        </nav>

        <button
          type="button"
          className="btn ghost"
          aria-expanded={menuOpen}
          aria-label="Open menu"
          style={{ display: 'none' }}
          onClick={() => setMenuOpen((v) => !v)}
        >
          Menu
        </button>
      </div>
    </header>
  );
}
